import React, { useState, useRef, useEffect, useContext } from "react";
import { IonCol, IonInput, IonItem, IonLabel, IonRow } from "@ionic/react";
import { FormGroup, Input } from "reactstrap";
import { Contact, Owner, Rider, Trainer } from "../../../models";
import { PersonContext } from "../../../context/PersonContext";
import RequiredInputIndicator from "../../Forms/RequiredInputIndicator";
import { FormFieldsInlineSkeleton } from "../../Skeletons/EntrySectionSkeletons";
import PhoneNumberTypeSelect from "../../Contact/PhoneNumberTypeSelect";
import PhoneInput from "react-phone-number-input";
import { CreateContactInput, UpdateContactInput, UpdateOwnerInput, UpdateRiderInput, UpdateTrainerInput } from "../../../API";
import { createContact, getContactById, updateContact } from "../../../utilities/contact/Contact";
import { updateRider } from "../../../utilities/rider/Rider";
import { updateOwner } from "../../../utilities/owner/Owner";
import { updateTrainer } from "../../../utilities/trainer/Trainer";
import { Result } from "../../../interfaces/Result";
import { formatTwilioNumber } from "../../../utilities/contact/FormatPhoneNumber";
import ErrorAlert from "../../Errors/ErrorAlert";

interface _Props {
    requiredFields?: string[];
    rider?: Rider | null;
    secondRider?: Rider | null;
    owner?: Owner | null;
    trainer?: Trainer | null;
    selectedContactId?: string | null;
    onSelectContact: (contact?: Contact, areAllRequiredFieldsComplete?: Boolean) => void;
    onSavingStateChange?: (isSaving: boolean) => void;
    onFieldChange?: (contact?: Contact, areAllRequiredFieldsComplete?: Boolean) => void;
    onRegisterSave?: (fn: () => Promise<void> | void) => void;
}
interface ContactFormState {
    contactType: string;
    name: string;
    email: string;
    phoneType: string;
    phoneNumber: any;
}

const PrimaryContactSection: React.FC<_Props> = ({
    requiredFields,
    rider,
    secondRider,
    owner,
    trainer,
    selectedContactId,
    onSelectContact,
    onSavingStateChange,
    onFieldChange,
    onRegisterSave,
}) => {
    const user = useContext(PersonContext);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState("");

    // Add ref for click outside detection
    const contactComponentRef = useRef<HTMLIonRowElement>(null);

    // Add ref to track the current contact state for the click outside handler
    const selectedContactRef = useRef<Contact | null | undefined>();

    // selectedContactState triggers re-renders and allows us to bind data to the UI
    const [selectedContactState, setSelectedContactState] = useState<Contact | null | undefined>();
    const [formState, setFormState] = useState<ContactFormState>({
        contactType: "",
        name: "",
        email: "",
        phoneType: "-",
        phoneNumber: "",
    });

    // Cache the last successfully saved contact by type to avoid stale/empty UI
    const [lastSavedContactByType, setLastSavedContactByType] = useState<Record<string, Contact | null>>({});

    // Track previous values to detect which section changed
    const [prevRider, setPrevRider] = useState<Rider | null | undefined>(rider);
    const [prevSecondRider, setPrevSecondRider] = useState<Rider | null | undefined>(secondRider);
    const [prevOwner, setPrevOwner] = useState<Owner | null | undefined>(owner);
    const [prevTrainer, setPrevTrainer] = useState<Trainer | null | undefined>(trainer);

    useEffect(() => {
        // Check if the currently selected contact type's corresponding section was changed
        const currentContactType = formState.contactType;
        let shouldClearContactType = false;

        // Only clear if the currently selected contact type's section was changed
        if (currentContactType === "owner" && owner && prevOwner && owner.id !== prevOwner.id) {
            shouldClearContactType = true;
        } else if (currentContactType === "owner" && !owner && prevOwner) {
            shouldClearContactType = true;
        } else if (currentContactType === "rider" && rider && prevRider && rider.id !== prevRider.id) {
            shouldClearContactType = true;
        } else if (currentContactType === "rider" && !rider && prevRider) {
            shouldClearContactType = true;
        } else if (currentContactType === "secondRider" && secondRider && prevSecondRider && secondRider.id !== prevSecondRider.id) {
            shouldClearContactType = true;
        } else if (currentContactType === "secondRider" && !secondRider && prevSecondRider) {
            shouldClearContactType = true;
        } else if (currentContactType === "trainer" && trainer && prevTrainer && trainer.id !== prevTrainer.id) {
            shouldClearContactType = true;
        } else if (currentContactType === "trainer" && !trainer && prevTrainer) {
            shouldClearContactType = true;
        }

        if (shouldClearContactType) {
            setSelectedContactState(undefined);
            const emptyFormState: ContactFormState = {
                contactType: "",
                name: "",
                email: "",
                phoneType: "-",
                phoneNumber: "",
            };
            setFormState(emptyFormState);
            selectedContactRef.current = undefined;
            onFieldChange?.(undefined, false);
            onSelectContact(undefined, false);
        }

        setPrevOwner(owner);
        setPrevRider(rider);
        setPrevSecondRider(secondRider);
        setPrevTrainer(trainer);
    }, [rider, secondRider, owner, trainer, formState.contactType]);

    // Check for a pre-saved Contact from the parent component (ex: already completed Entry)
    useEffect(() => {
        const getData = async () => {
            setIsLoading(true);
            let selectedContact: Contact | null = null;

            if (selectedContactId) {
                const selectedContactResult: Result = await getContactById(selectedContactId);
                if (selectedContactResult.isSuccess) {
                    selectedContact = selectedContactResult.result;
                } else {
                }
            }

            if (selectedContact) {
                // If the parent component DOES pass in a pre-selected Contact, then set all values
                // Determine which contact type this is by checking against owner, rider, secondRider, and trainer
                let determinedContactType = "other";

                // Check by contact ID first (most reliable)
                if (owner && (owner.ownerContactId === selectedContact.id || owner.contact?.id === selectedContact.id)) {
                    determinedContactType = "owner";
                } else if (rider && (rider.riderContactId === selectedContact.id || rider.contact?.id === selectedContact.id)) {
                    determinedContactType = "rider";
                } else if (secondRider && (secondRider.riderContactId === selectedContact.id || secondRider.contact?.id === selectedContact.id)) {
                    determinedContactType = "secondRider";
                } else if (trainer && (trainer.trainerContactId === selectedContact.id || trainer.contact?.id === selectedContact.id)) {
                    determinedContactType = "trainer";
                } else {
                    // Fallback: check by name match if contact IDs don't match
                    // This handles cases where contact was selected but not yet linked to owner/rider/trainer
                    if (owner && owner.name && selectedContact.name && owner.name.toLowerCase() === selectedContact.name.toLowerCase()) {
                        determinedContactType = "owner";
                    } else if (rider && rider.name && selectedContact.name && rider.name.toLowerCase() === selectedContact.name.toLowerCase()) {
                        determinedContactType = "rider";
                    } else if (secondRider && secondRider.name && selectedContact.name && secondRider.name.toLowerCase() === selectedContact.name.toLowerCase()) {
                        determinedContactType = "secondRider";
                    } else if (trainer && trainer.name && selectedContact.name && trainer.name.toLowerCase() === selectedContact.name.toLowerCase()) {
                        determinedContactType = "trainer";
                    }
                }

                setSelectedContactState(selectedContact);
                const currentFormState: ContactFormState = {
                    contactType: determinedContactType,
                    name: selectedContact.name || "",
                    email: selectedContact.personalEmail || "",
                    phoneType: getPhoneTypeFromContact(selectedContact),
                    phoneNumber: getPhoneNumberFromContact(selectedContact),
                };

                setFormState(currentFormState);

                // Update the cache for the determined contact type
                if (determinedContactType !== "other") {
                    setLastSavedContactByType((prev) => ({ ...prev, [determinedContactType]: selectedContact }));
                }

                // Evaluate if this pre-selected Contact has all required fields in order to be considered "Complete"
                const isSectionValid = checkIfSectionIsValid(currentFormState);

                if (onFieldChange) onFieldChange(selectedContact, isSectionValid);
                if (onSelectContact) onSelectContact(selectedContact, isSectionValid);
            } else {
                // If the parent component does NOT pass in a pre-selected Contact, then clear all values

                setSelectedContactState(undefined);
                setFormState({
                    contactType: "",
                    name: "",
                    email: "",
                    phoneType: "-",
                    phoneNumber: "",
                });
            }

            setIsLoading(false);
        };

        getData();
    }, [selectedContactId, owner, rider, secondRider, trainer]);

    // Keep selectedContactRef updated
    useEffect(() => {
        selectedContactRef.current = selectedContactState;
    }, [selectedContactState]);

    // Register save fn with parent
    useEffect(() => {
        if (!onRegisterSave) return;
        const fn = () => {
            if (selectedContactRef.current) {
                const currentContact = selectedContactRef.current;
                const hasChanges =
                    (formState.name && formState.name !== currentContact.name) ||
                    (formState.email && formState.email !== currentContact.personalEmail) ||
                    (formState.phoneType && formState.phoneType === "cell" && formState.phoneNumber !== currentContact.cell) ||
                    (formState.phoneType && formState.phoneType === "home" && formState.phoneNumber !== currentContact.home) ||
                    (formState.phoneType && formState.phoneType === "work" && formState.phoneNumber !== currentContact.work);
                if (hasChanges) {
                    return handleSaveContactData(currentContact);
                }
            } else if (formState.name) {
                return handleSaveContactData();
            }
        };
        onRegisterSave(fn);
    }, [onRegisterSave, formState, selectedContactRef.current]);

    const checkIfFieldIsRequired = (fieldName: string) => {
        if (requiredFields && requiredFields.length > 0) {
            const index = requiredFields.findIndex((field) => field.toLocaleLowerCase() === fieldName.toLocaleLowerCase());
            if (index > -1) return true;
        }
        return false;
    };

    const checkIfSectionIsValid = (currentFormState: ContactFormState) => {
        const missingFields: string[] = [];
        if (checkIfFieldIsRequired("type") && (currentFormState.contactType === "" || currentFormState.contactType === "-")) missingFields.push("type");
        if (checkIfFieldIsRequired("name") && currentFormState.name === "") missingFields.push("name");
        if (checkIfFieldIsRequired("email") && currentFormState.email === "") missingFields.push("email");
        if (checkIfFieldIsRequired("phonenumbertype") && (currentFormState.phoneType === "" || currentFormState.phoneType === "-")) missingFields.push("phoneNumberType");
        if (checkIfFieldIsRequired("phoneNumber") && currentFormState.phoneNumber === "") missingFields.push("phoneNumber");

        const isValid = missingFields.length === 0;

        return isValid;
    };

    const handleFieldChange = (updatedFormState: ContactFormState) => {
        if (onFieldChange) {
            const isSectionValid = checkIfSectionIsValid(updatedFormState);
            onFieldChange(selectedContactState || undefined, isSectionValid);
        }
    };

    const getPhoneTypeFromContact = (contactObject: Contact) => {
        return contactObject.cell ? "cell" : contactObject.home ? "home" : contactObject.work ? "work" : "";
    };

    const getPhoneNumberFromContact = (contactObject: Contact) => {
        return contactObject.cell || contactObject.home || contactObject.work || "";
    };

    const handleContactTypeChange = async (newContactType: string) => {
        setIsLoading(true);
        setError("");

        let latestContact: Contact | null = null;
        let updatedFormState: ContactFormState = {
            contactType: newContactType,
            name: "",
            email: "",
            phoneType: "-",
            phoneNumber: "",
        };

        if (newContactType === "owner") {
            const mappedContact = lastSavedContactByType["owner"];
            if ((mappedContact && owner?.ownerContactId && owner?.ownerContactId === mappedContact.id) || mappedContact?.name === owner?.name) {
                latestContact = mappedContact;
            } else if (owner?.contact) {
                latestContact = owner.contact;
            } else if (owner?.ownerContactId) {
                const res: Result = await getContactById(owner.ownerContactId);
                if (res.isSuccess) {
                    latestContact = res.result as Contact;
                    setLastSavedContactByType((prev) => ({ ...prev, [newContactType]: latestContact || null }));
                }
            }
            updatedFormState = {
                contactType: newContactType,
                name: owner?.name || "",
                email: latestContact?.personalEmail || "",
                phoneType: latestContact ? getPhoneTypeFromContact(latestContact) : "-",
                phoneNumber: latestContact ? getPhoneNumberFromContact(latestContact) : "",
            };
        } else if (newContactType === "rider") {
            const mappedContact = lastSavedContactByType["rider"];
            if ((mappedContact && rider?.riderContactId && rider?.riderContactId === mappedContact.id) || mappedContact?.name === rider?.name) {
                latestContact = mappedContact;
            } else if (rider?.contact) {
                latestContact = rider.contact;
            } else if (rider?.riderContactId) {
                const res: Result = await getContactById(rider.riderContactId);
                if (res.isSuccess) {
                    latestContact = res.result as Contact;
                    setLastSavedContactByType((prev) => ({ ...prev, [newContactType]: latestContact || null }));
                }
            }
            updatedFormState = {
                contactType: newContactType,
                name: rider?.name || "",
                email: latestContact?.personalEmail || "",
                phoneType: latestContact ? getPhoneTypeFromContact(latestContact) : "-",
                phoneNumber: latestContact ? getPhoneNumberFromContact(latestContact) : "",
            };
        } else if (newContactType === "secondRider") {
            const mappedContact = lastSavedContactByType["secondRider"];
            if ((mappedContact && secondRider?.riderContactId && secondRider?.riderContactId === mappedContact.id) || mappedContact?.name === secondRider?.name) {
                latestContact = mappedContact;
            } else if (secondRider?.contact) {
                latestContact = secondRider.contact;
            } else if (secondRider?.riderContactId) {
                const res: Result = await getContactById(secondRider.riderContactId);
                if (res.isSuccess) {
                    latestContact = res.result as Contact;
                    setLastSavedContactByType((prev) => ({ ...prev, [newContactType]: latestContact || null }));
                }
            }
            updatedFormState = {
                contactType: newContactType,
                name: secondRider?.name || "",
                email: latestContact?.personalEmail || "",
                phoneType: latestContact ? getPhoneTypeFromContact(latestContact) : "-",
                phoneNumber: latestContact ? getPhoneNumberFromContact(latestContact) : "",
            };
        } else if (newContactType === "trainer") {
            const mappedContact = lastSavedContactByType["trainer"];
            if ((mappedContact && trainer?.trainerContactId && trainer?.trainerContactId === mappedContact.id) || mappedContact?.name === trainer?.name) {
                latestContact = mappedContact;
            } else if (trainer?.contact) {
                latestContact = trainer.contact;
            } else if (trainer?.trainerContactId) {
                const res: Result = await getContactById(trainer.trainerContactId);
                if (res.isSuccess) {
                    latestContact = res.result as Contact;
                    setLastSavedContactByType((prev) => ({ ...prev, [newContactType]: latestContact || null }));
                }
            }
            updatedFormState = {
                contactType: newContactType,
                name: trainer?.name || "",
                email: latestContact?.personalEmail || "",
                phoneType: latestContact ? getPhoneTypeFromContact(latestContact) : "-",
                phoneNumber: latestContact ? getPhoneNumberFromContact(latestContact) : "",
            };
        }

        setLastSavedContactByType((prev) => ({ ...prev, [newContactType]: latestContact || null }));

        setSelectedContactState(latestContact || undefined);
        setFormState(updatedFormState);
        handleFieldChange(updatedFormState);
        const isSectionValid = checkIfSectionIsValid(updatedFormState);
        // Call the prop function to update the parent state
        if (onSelectContact) onSelectContact(latestContact || undefined, isSectionValid);

        setIsLoading(false);
    };

    const handleNameChange = (newName: string) => {
        setError("");
        const updatedFormState: ContactFormState = {
            contactType: formState.contactType,
            name: newName,
            email: formState.email,
            phoneType: formState.phoneType,
            phoneNumber: formState.phoneNumber,
        };
        setFormState((prev) => ({
            ...prev,
            name: newName,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleEmailChange = (newEmail: string) => {
        setError("");
        const updatedFormState: ContactFormState = {
            contactType: formState.contactType,
            name: formState.name,
            email: newEmail,
            phoneType: formState.phoneType,
            phoneNumber: formState.phoneNumber,
        };
        setFormState((prev) => ({
            ...prev,
            email: newEmail,
        }));
        handleFieldChange(updatedFormState);
    };

    const handlePhoneTypeChange = (newPhoneType: string) => {
        setError("");
        const updatedFormState: ContactFormState = {
            contactType: formState.contactType,
            name: formState.name,
            email: formState.email,
            phoneType: newPhoneType,
            phoneNumber: formState.phoneNumber,
        };
        setFormState((prev) => ({
            ...prev,
            phoneType: newPhoneType,
        }));
        handleFieldChange(updatedFormState);
    };

    const handlePhoneNumberChange = (newPhoneNumber: string) => {
        setError("");
        const updatedFormState: ContactFormState = {
            contactType: formState.contactType,
            name: formState.name,
            email: formState.email,
            phoneType: formState.phoneType,
            phoneNumber: newPhoneNumber,
        };
        setFormState((prev) => ({
            ...prev,
            phoneNumber: newPhoneNumber,
        }));
        handleFieldChange(updatedFormState);
    };

    const handleSaveContactData = async (contactNeedingUpdate?: Contact | null | undefined) => {
        setError("");

        // If this function is called before any contact was selected or a name was selected, don't perform any actions.
        if (!contactNeedingUpdate && !formState.name) {
            return;
        }

        // Do not save data if the section is not valid (all required fields must be completed)
        const isSectionValid = checkIfSectionIsValid(formState);
        if (!isSectionValid) {
            return;
        }

        if (onSavingStateChange) onSavingStateChange(true);

        // Track the updatedContact
        let updatedContact: Contact | null | undefined = contactNeedingUpdate;

        // First, check if this contact already exists.
        if (contactNeedingUpdate) {
            // Next, check what actually needs to be update in order to avoid an update if the values for the contact were not changed.
            let shouldUpdateContactInDB = false;
            let updateInput: UpdateContactInput = {
                id: contactNeedingUpdate.id,
            };
            if (formState.name && formState.name !== contactNeedingUpdate.name) {
                shouldUpdateContactInDB = true;
                updateInput["name"] = formState.name;
            }
            if (formState.email && formState.email !== contactNeedingUpdate.personalEmail) {
                shouldUpdateContactInDB = true;
                updateInput["personalEmail"] = formState.email;
            }
            if (formState.phoneType === "cell" && formState.phoneNumber !== contactNeedingUpdate.cell) {
                shouldUpdateContactInDB = true;
                updateInput["cell"] = formatTwilioNumber(formState.phoneNumber);
            }
            if (formState.phoneType === "home" && formState.phoneNumber !== contactNeedingUpdate.home) {
                shouldUpdateContactInDB = true;
                updateInput["home"] = formatTwilioNumber(formState.phoneNumber);
            }
            if (formState.phoneType === "work" && formState.phoneNumber !== contactNeedingUpdate.work) {
                shouldUpdateContactInDB = true;
                updateInput["work"] = formatTwilioNumber(formState.phoneNumber);
            }

            if (shouldUpdateContactInDB) {
                // Run the following command to update the contact in the DB
                const updateResult = await updateContact(updateInput);
                if (updateResult.isSuccess) {
                    updatedContact = updateResult.result as Contact;
                } else {
                    const errorMessage = "Sorry, a problem occurred and the contact could not be updated. Please verify all fields are valid. " + updateResult.message;
                    setError(errorMessage);
                }
            }
        }

        // OTW, check if this is a new contact that needs to be created.
        else if (formState.name) {
            let createInput: CreateContactInput = {
                name: formState.name,
                personalEmail: formState.email,
                createdBy: user.id,
            };
            if (formState.phoneType === "cell" && formState.phoneNumber) createInput["cell"] = formatTwilioNumber(formState.phoneNumber);
            if (formState.phoneType === "home" && formState.phoneNumber) createInput["home"] = formatTwilioNumber(formState.phoneNumber);
            if (formState.phoneType === "work" && formState.phoneNumber) createInput["work"] = formatTwilioNumber(formState.phoneNumber);

            const createResult = await createContact(createInput);
            if (createResult.isSuccess) {
                const newContact: Contact = createResult.result;
                updatedContact = newContact;

                // Update the Owner / Rider / Second Rider / Trainer with the new contact info
                if (formState.contactType === "owner" && owner && !owner.contact) {
                    const updateOwnerInput: UpdateOwnerInput = {
                        id: owner.id,
                        ownerContactId: newContact.id,
                    };
                    await updateOwner(updateOwnerInput);
                } else if (formState.contactType === "rider" && rider && !rider.contact) {
                    const updateRiderInput: UpdateRiderInput = {
                        id: rider.id,
                        riderContactId: newContact.id,
                    };
                    await updateRider(updateRiderInput);
                } else if (formState.contactType === "secondRider" && secondRider && !secondRider.contact) {
                    const updateRiderInput: UpdateRiderInput = {
                        id: secondRider.id,
                        riderContactId: newContact.id,
                    };
                    await updateRider(updateRiderInput);
                } else if (formState.contactType === "trainer" && trainer && !trainer.contact) {
                    const updateTrainerInput: UpdateTrainerInput = {
                        id: trainer.id,
                        trainerContactId: newContact.id,
                    };
                    const updateResult = await updateTrainer(updateTrainerInput);
                }
            } else {
                const errorMessage = "Sorry, a problem occurred and the contact could not be created. Please verify all fields are valid. " + createResult.message;
                setError(errorMessage);
            }
        }

        // After any successful create/update, normalize local form state to reflect saved values
        if (updatedContact) {
            const normalizedForm: ContactFormState = {
                contactType: formState.contactType,
                name: formState.name || updatedContact.name || "",
                email: formState.email || updatedContact.personalEmail || "",
                phoneType: getPhoneTypeFromContact(updatedContact) || formState.phoneType || "-",
                phoneNumber: getPhoneNumberFromContact(updatedContact) || formState.phoneNumber || "",
            };
            setFormState(normalizedForm);

            // Update last-saved cache and the ref for the active type
            setLastSavedContactByType((prev) => {
                const val = { ...prev, [formState.contactType]: updatedContact || null };
                return val;
            });
            selectedContactRef.current = updatedContact;
        }

        setSelectedContactState(updatedContact);

        // Inform parent with the freshest contact so it can update its model
        const isSectionValidAfterSave = checkIfSectionIsValid(formState);
        if (onSelectContact) onSelectContact(updatedContact || undefined, isSectionValidAfterSave);
        if (onSavingStateChange) onSavingStateChange(false);
    };

    return (
        <>
            <div data-section="contact">
                {error && <ErrorAlert error={error} width="12" />}
                <IonRow ref={contactComponentRef}>
                    <IonCol size="12">
                        <FormGroup>
                            <IonLabel position="stacked">
                                Contact Type
                                {checkIfFieldIsRequired("type") && <RequiredInputIndicator />}
                            </IonLabel>
                            <Input
                                name="contactType"
                                type="select"
                                value={formState.contactType}
                                onChange={(e: any) => {
                                    handleContactTypeChange(e.target.value);
                                }}>
                                <option>-</option>
                                <option value="owner">{owner?.name ? `Owner - ${owner.name}` : "Owner"}</option>
                                <option value="rider">{rider?.name ? `Rider - ${rider.name}` : "Rider"}</option>
                                {secondRider && <option value="secondRider">{secondRider?.name ? `Second Rider - ${secondRider.name}` : "Second Rider"}</option>}
                                <option value="trainer">{trainer?.name ? `Trainer - ${trainer.name}` : "Trainer"}</option>
                                <option value="other">Other</option>
                            </Input>
                        </FormGroup>
                    </IonCol>
                </IonRow>
            </div>

            {formState.contactType && formState.contactType !== "-" && (
                <>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">
                                    Full Name
                                    {checkIfFieldIsRequired("name") && <RequiredInputIndicator />}
                                </IonLabel>
                                <IonInput
                                    type="text"
                                    value={formState.name}
                                    aria-required={true}
                                    onIonChange={(e) => {
                                        handleNameChange(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>

                        <IonCol sizeXs="12" sizeMd="6">
                            <IonItem color="white">
                                <IonLabel position="stacked">
                                    Email
                                    {checkIfFieldIsRequired("email") && <RequiredInputIndicator />}
                                </IonLabel>
                                <IonInput
                                    type="email"
                                    value={formState.email}
                                    aria-required={true}
                                    onIonChange={(e) => {
                                        handleEmailChange(e.detail.value!);
                                    }}
                                />
                            </IonItem>
                        </IonCol>
                    </IonRow>
                    <IonRow>
                        <IonCol sizeXs="12" sizeMd="6">
                            <PhoneNumberTypeSelect
                                id="entry-form-phone-number-type-input"
                                isRequired={checkIfFieldIsRequired("phonenumbertype")}
                                selectedType={formState.phoneType}
                                onChange={handlePhoneTypeChange}
                            />
                        </IonCol>
                        <IonCol sizeXs="12" sizeMd="6">
                            <IonLabel position="stacked">
                                <p className="description text-primary ml-3 mb-0">
                                    Phone Number
                                    {checkIfFieldIsRequired("phoneNumber") && <RequiredInputIndicator />}
                                </p>
                            </IonLabel>
                            <PhoneInput
                                placeholder="Enter phone number"
                                defaultCountry="US"
                                autoFocus
                                value={formState.phoneNumber}
                                onChange={(phoneNumberValue?: any) => {
                                    handlePhoneNumberChange(phoneNumberValue ? phoneNumberValue.toString() : "");
                                }}
                            />
                        </IonCol>
                    </IonRow>
                </>
            )}

            {isLoading && (
                <IonRow>
                    <FormFieldsInlineSkeleton fieldCount={2} />
                </IonRow>
            )}
        </>
    );
};

export default PrimaryContactSection;
